import React, { useState } from "react";
import { Download, CheckCircle2, FileText, Shield, Loader2 } from "lucide-react";
import { getUtm } from "../utils/utm";
import { trackConversion } from "../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_PATH = "/downloads/renovatie-checklist.pdf";

/**
 * Lead-magnet form for the free "Renovatie­checklist — 12 fouten" PDF download.
 * Variant 'banner' for inline use on dienstpagina's / homepage.
 * Variant 'page' for dedicated /gratis-checklist landingspage.
 */
export function LeadMagnetForm({ variant = "banner", sourcePage = "", className = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", consent: true });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email) {
      setError("Vul uw naam en e-mail in om de checklist te ontvangen.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/lead-magnet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || null,
          source_page: sourcePage || (typeof window !== "undefined" ? window.location.pathname : ""),
          marketing_consent: form.consent,
          utm: getUtm(),
        }),
      });
      if (!res.ok) throw new Error("Er ging iets mis. Probeer het opnieuw.");
      trackConversion("lead_magnet", 15, { magnet: "renovatie-checklist" });
      setSubmitted(true);
      // Auto trigger download
      const a = document.createElement("a");
      a.href = PDF_PATH;
      a.download = "renovatie-checklist.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err.message || "Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="lead-magnet-success"
        className={`relative overflow-hidden border border-koning-gold/40 bg-koning-surface p-8 md:p-10 ${className}`}
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-1 w-10 h-10 rounded-full bg-koning-gold/15 flex items-center justify-center">
            <CheckCircle2 className="text-koning-gold" size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-2xl md:text-3xl text-white mb-3">
              Bedankt! Uw checklist staat klaar.
            </h3>
            <p className="text-koning-muted mb-6 leading-relaxed">
              De download is automatisch gestart. Werkt het niet? Klik dan op onderstaande knop. We sturen u
              binnenkort ook een kopie per e-mail samen met praktische renovatie­tips — uitschrijven kan altijd.
            </p>
            <a
              href={PDF_PATH}
              download="renovatie-checklist.pdf"
              data-testid="lead-magnet-manual-download"
              className="inline-flex items-center gap-2 bg-koning-gold text-koning-bg font-semibold px-6 py-3 hover:bg-koning-goldHover transition-colors"
            >
              <Download size={18} /> Download PDF opnieuw
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`lead-magnet-${variant}`}
      className={`relative overflow-hidden border border-koning-border bg-gradient-to-br from-koning-surface2 to-koning-bg p-6 md:p-10 ${className}`}
    >
      {/* Decorative gold accent */}
      <div aria-hidden className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-koning-gold/10 blur-3xl" />

      <div className={`relative grid ${variant === "page" ? "md:grid-cols-2 gap-10" : "md:grid-cols-[1.1fr_1fr] gap-8"} items-center`}>
        {/* LEFT — pitch */}
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-koning-gold mb-4">
            <FileText size={14} /> Gratis download · PDF
          </div>
          <h2 className={`font-display text-koning-gold tracking-tight ${variant === "page" ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"} mb-4`}>
            Renovatie­checklist:<br />
            <span className="text-koning-gold">12 fouten die u duizenden euro&apos;s kunnen kosten</span>
          </h2>
          <p className="text-[#E5D5A6] leading-relaxed mb-5">
            Voorkom dure verrassingen tijdens uw verbouwing. Deze gids — gebaseerd op onze praktijkervaring in
            Zuid-Holland — geeft u per fout het risico én concrete preventie­tips om mee te nemen
            naar elk offertegesprek.
          </p>
          <ul className="space-y-2 text-sm text-[#E5D5A6]">
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-koning-gold mt-0.5 shrink-0" /> 12 dure valkuilen met het risico per fout</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-koning-gold mt-0.5 shrink-0" /> Vragen om aan elke aannemer te stellen</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-koning-gold mt-0.5 shrink-0" /> Direct printbaar voor offertegesprekken</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-koning-gold mt-0.5 shrink-0" /> Editie 2026 — actuele subsidies & BTW-tarieven</li>
          </ul>
        </div>

        {/* RIGHT — form */}
        <form onSubmit={submit} className="space-y-3" data-testid="lead-magnet-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-koning-gold mb-1.5">Naam *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={update("name")}
              data-testid="lead-magnet-name"
              placeholder="Uw volledige naam"
              className="w-full bg-black/40 border border-koning-gold/30 px-4 py-3 text-koning-gold placeholder-koning-gold/40 focus:outline-none focus:border-koning-gold transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-koning-gold mb-1.5">E-mail *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid="lead-magnet-email"
              placeholder="naam@voorbeeld.nl"
              className="w-full bg-black/40 border border-koning-gold/30 px-4 py-3 text-koning-gold placeholder-koning-gold/40 focus:outline-none focus:border-koning-gold transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-koning-gold mb-1.5">Telefoon (optioneel)</label>
            <input
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              data-testid="lead-magnet-phone"
              placeholder="06 - 12 34 56 78"
              className="w-full bg-black/40 border border-koning-gold/30 px-4 py-3 text-koning-gold placeholder-koning-gold/40 focus:outline-none focus:border-koning-gold transition-colors"
            />
          </div>

          <label className="flex items-start gap-2 text-xs text-[#E5D5A6] cursor-pointer pt-1">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={update("consent")}
              data-testid="lead-magnet-consent"
              className="mt-0.5 accent-koning-gold"
            />
            <span>Ik ontvang ook graag periodiek renovatie­tips per e-mail. Uitschrijven kan altijd met één klik.</span>
          </label>

          {error && (
            <div data-testid="lead-magnet-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="lead-magnet-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-koning-gold text-koning-bg font-semibold px-6 py-3.5 hover:bg-koning-goldHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Bezig...</>
            ) : (
              <><Download size={18} /> Download de checklist</>
            )}
          </button>

          <div className="flex items-center gap-2 text-[11px] text-[#E5D5A6] justify-center pt-1">
            <Shield size={12} /> Wij gaan zorgvuldig met uw gegevens om — zie onze privacyverklaring
          </div>
        </form>
      </div>
    </div>
  );
}
